import {useMemo, type FC} from 'react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {Organization, Enterprise} from '@github-ui/repos-types'
import type {Repository} from '@github-ui/current-repository'
import {useBranches} from '../../hooks/use-branches'
import {UserFilterProvider} from './providers/user'
import {RuleStatusFilterProvider} from './providers/rule-status'
import {ShaFilterProvider} from './providers/sha'
import {
  SecurityProductsFilterBar,
  parseAsUser,
  serializeLogin,
  filterDefinition,
  timePeriodFilterDefinition,
  evaluateStatusFilterDefinition,
  branchFilterDefinition,
  repositoryFilterDefinition,
  staticOrganizationFilterDefinition,
  rulesetFilterDefinition,
  type FilterDefinition,
} from './SecurityProductsFilterBar'
import type {InsightsFilter, Ruleset, SourceType} from '../../types/rules-types'
import {insightsIndexPath} from '../../helpers/insights-filter'
import {useIsStafftools} from '../../hooks/use-is-stafftools'
import {useInsightsActors} from '../../hooks/use-insights-actors'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'

type InsightsFilterBar2Props = {
  branchIsValid?: boolean
  filter: InsightsFilter
  source: Repository | Organization | Enterprise
  sourceType: SourceType
  organizations?: string[]
  rulesets: Ruleset[]
}

export const InsightsFilterBar2: FC<InsightsFilterBar2Props> = ({
  branchIsValid,
  filter,
  source,
  sourceType,
  organizations,
  rulesets,
}) => {
  const rulesInsightsFilterBarSha = useFeatureFlag('rules_insights_filter_bar_sha')
  const rulesInsightsFilterBarShaOrgAndBiz = useFeatureFlag('rules_insights_filter_bar_sha_org_and_biz')
  const isStafftools = useIsStafftools()
  const isRepoNonStafftools = sourceType === 'repository' && !isStafftools
  const repo = source as Repository
  const fetchedBranches = useBranches(isRepoNonStafftools ? repo.ownerLogin : '', isRepoNonStafftools ? repo.name : '')
  // If the server confirmed the branch from the URL is valid, include it in the list
  // so validation passes even before the async branch fetch completes.
  // Invalid branches are excluded so the user sees a validation error.
  const branches = useMemo(() => {
    if (!branchIsValid || !filter.branch || fetchedBranches.includes(filter.branch)) return fetchedBranches
    return [filter.branch, ...fetchedBranches]
  }, [branchIsValid, fetchedBranches, filter.branch])
  const showShaFilter =
    (sourceType === 'repository' && rulesInsightsFilterBarSha) ||
    ((sourceType === 'organization' || sourceType === 'enterprise') && rulesInsightsFilterBarShaOrgAndBiz)
  const {users: actors} = useInsightsActors()
  const {resolvePath} = useRelativeNavigation()
  const repoSuggestionsUrl = resolvePath('repo_suggestions')

  const filterDefinitions = useMemo<Array<FilterDefinition<InsightsFilter>>>(
    () => [
      timePeriodFilterDefinition<InsightsFilter>(),
      evaluateStatusFilterDefinition<InsightsFilter>(),
      ...(isRepoNonStafftools ? [branchFilterDefinition<InsightsFilter>(branches)] : []),
      ...(sourceType === 'organization' ? [repositoryFilterDefinition<InsightsFilter>(repoSuggestionsUrl)] : []),
      ...(sourceType === 'enterprise' && organizations?.length
        ? [staticOrganizationFilterDefinition<InsightsFilter>(organizations)]
        : []),
      rulesetFilterDefinition<InsightsFilter>(rulesets),
      filterDefinition<InsightsFilter>({
        config: {
          filterKey: 'actor',
          queryKey: 'user',
          parse: parseAsUser,
          serialize: (v: unknown) => serializeLogin(v as {login: string}),
        },
        provider: new UserFilterProvider(actors),
      }),
      filterDefinition<InsightsFilter>({
        config: {filterKey: 'ruleStatus', queryKey: 'rule-status'},
        provider: new RuleStatusFilterProvider(),
      }),
      ...(showShaFilter
        ? [
            filterDefinition<InsightsFilter>({
              config: {filterKey: 'afterOid', queryKey: 'sha'},
              provider: new ShaFilterProvider(),
            }),
          ]
        : []),
    ],
    [rulesets, sourceType, showShaFilter, organizations, branches, isRepoNonStafftools, actors, repoSuggestionsUrl],
  )

  return (
    <SecurityProductsFilterBar<InsightsFilter>
      id="rules-insights-filter"
      label="Filter rules insights"
      filter={filter}
      filterDefinitions={filterDefinitions}
      getIndexPath={insightsIndexPath}
    />
  )
}
